import React, { useContext, useReducer } from "react";
import FoldersContext from "./FoldersContext";
import FoldersContextReducers from './FoldersContextReducer';
import { 
  initializeFolders,
  setFoldersCurrentPath,
  setFolderTree,
  setSnackbar,
} from "../actions/folderActions";
import { FolderService } from "@api/folder";
import AuthContext from "@context/auth/AuthContext";
import MainContext from "@context/main/MainContext";
import {
  traverseAndProcessTree,
  updateItemFavorite,
  findParent,
  traverseAndUpdate,
  deleteTargetModel,
  updateFolderName,
  deleteTargetFolders,
  extractFolderNames
} from '@utils/mmmListViewUtils';

const initialState = {
  folderTree: [],
  currentPath: [],
  snackbar: { status: '', message: '', open: false }
};

// eslint-disable-next-line react/prop-types
const FolderTreeProvider = ({ children }) => {
  const authContext = useContext(AuthContext);
  const mainContext = useContext(MainContext);
  const folderService = new FolderService();
  const [state, dispatch] = useReducer(FoldersContextReducers, {
    ...initialState,
  });

  const getFolderTree = () => {
    return state.folderTree;
  }
  const getCurrentPath = () => {
    return state.currentPath;
  }
  const setCurrentPath = (newCurrentPath) => {
    dispatch(setFoldersCurrentPath(newCurrentPath));
  }
  const refreshCurrentPath = () => {
    dispatch(setFoldersCurrentPath(initialState.currentPath));
  }
  const extendCurrentPath = (folderId) => {
    const finedFolderAndPath = traverseAndProcessTree(
      state.folderTree, 
      { folderInfoId: folderId}
    );
    
    const folderStack = [
      state.currentPath[0],
      ...finedFolderAndPath.path
    ];
    setCurrentPath(folderStack);

    return folderStack;
  };
  const updatePathToBreadcrumb = (index) => {
    const sliceFolderStack = state.currentPath.slice(0, index + 1);
    setCurrentPath(sliceFolderStack);
  }
  const updateFolderTreeWithAction = (
    item, 
    actionFunction, 
    ...args
  ) => {
    const finedFolderParent = findParent(
      state.folderTree, 
      item.id, 
      state.currentPath
    );

    const updatedModelTree = traverseAndUpdate(
      state.folderTree, 
      finedFolderParent.slice(0, -1), 
      actionFunction, 
      ...args
    )

    dispatch(setFolderTree(updatedModelTree));
  }
  const setFoldersAndPath = (folders, currentPath) => {
    const foldersInfo = {folders, currentPath};
    dispatch(initializeFolders(foldersInfo));
  };
  const createNewFolder = async (folderName) => {
    const folderNames = extractFolderNames(state.folderTree);

    if (folderNames.includes(folderName)) {
      return true;
    } else {
      const currentParentFolder = state.currentPath[state.currentPath.length - 1];
      const folderDetail = { folderName, currentParentFolder};

      try {

        const token = await authContext.getIdToken();
        const newFolder = await folderService.createNewFolder(token, folderDetail);
        const updatedFolderTree = traverseAndProcessTree(
          state.folderTree,
          { 
            targetFolder: folderDetail.currentParentFolder, 
            insertItem: newFolder
          }
        );
        
        dispatch(setFolderTree(updatedFolderTree));
        return false;
      } catch (error) {
        console.error(error);
        return true;
      }
    }
  }
  const moveItemToFolder = async (targetFolder, items) => {
    const moveItemDetails = {
      targetFolderId: targetFolder.id,
      items: items,
    }
    
    const token = await authContext.getIdToken();
    const data = await folderService.moveToFolder(token, moveItemDetails);

    items.forEach((item) => {
      if (data.item_statuses[item.id][2] === 200) {
        const currentPath = findParent(
          state.folderTree,
          item.parentId || 0,
          state.currentPath
        );
        const updatedFolderTree = traverseAndUpdate(
          state.folderTree,
          currentPath,
          deleteTargetModel,
          item
        );
        const updatedFolderTreeAfterIns = traverseAndProcessTree(
          updatedFolderTree,
          {
            targetFolder,
            insertItem: item,
          }
        );
        dispatch(setFolderTree(updatedFolderTreeAfterIns));
      }
    });
  }
  const renameFolder = async (newFolderName, targetFolder) => {
    const folderDetails = {
      newFolderName,
      folderId: targetFolder.id,
    }

    const token = await authContext.getIdToken();
    await folderService.renameFolder(token, folderDetails);
    
    const oldFolderName = targetFolder.folder_name;

    updateFolderTreeWithAction(targetFolder, updateFolderName, targetFolder, newFolderName);

    const snackbarMessage = `Successfully renamed ${oldFolderName} to ${newFolderName}.`;
    constructSnackbar(snackbarMessage, 'success');
  }

  const removeFoldersAndReconnectChildren = async (foldersToDelete) => {
    const folderToDeleteParams = {
      foldersToDelete: foldersToDelete,
      countryCode: authContext.userCountryCode, 
      kwargs: mainContext.getPostKWArgs()
    }

    const token = await authContext.getIdToken();
    const data = await folderService.removeFolder(token, folderToDeleteParams);

    const successfullyDeletedFolders = foldersToDelete.filter((folder) => { 
      return data.folder_statuses[folder.id][1] === 200; 
    });

    if (successfullyDeletedFolders.length === 0) {
      return;
    }

    updateFolderTreeWithAction(successfullyDeletedFolders[0], deleteTargetFolders, successfullyDeletedFolders);

    if (successfullyDeletedFolders.length === 1) {
      const snackbarMessage = `${successfullyDeletedFolders[0].folder_name} has been successfully deleted.`;
      constructSnackbar(snackbarMessage, 'success');
    }

    if (successfullyDeletedFolders.length > 1) {
      const snackbarMessage = `Selected folders (${successfullyDeletedFolders.length} total) have been successfully deleted.`
      constructSnackbar(snackbarMessage, 'success');
    }
  }
  const updateFolderOrItemFavoriteStatus = (targetItem) => {
    updateFolderTreeWithAction(targetItem, updateItemFavorite, targetItem);
  }
  const getSnackbar = () => {
    return state.snackbar;
  }
  const constructSnackbar = (message, status) => {
    const snackbar = { message, status, open: true};
    dispatch(setSnackbar(snackbar));
  };
  const refreshSnackbar = () => {
    dispatch(setSnackbar(initialState.snackbar));
  }

  return (
    <FoldersContext.Provider 
      value={{
        getFolderTree,
        getCurrentPath,
        setCurrentPath,
        refreshCurrentPath,
        getSnackbar,
        constructSnackbar,
        refreshSnackbar,
        setFoldersAndPath,
        createNewFolder,
        moveItemToFolder,
        renameFolder,
        removeFoldersAndReconnectChildren,
        updateFolderOrItemFavoriteStatus,
        updateFolderTreeWithAction,
        extendCurrentPath,
        updatePathToBreadcrumb,
      }}
    >
      {children}
    </FoldersContext.Provider>
  );
}

export default FolderTreeProvider;